import React from "react";
import { RoundQuestionMarkIcon } from "../../../assets/icons";

type CardDot = { colorClass: string; label: string };
type CardBreakdown = {
  colorClass: string;
  label: string | React.ReactNode;
  amount: string | React.ReactNode;
};

export interface PortfolioCardData {
  id: string;
  title: string;
  value: string | React.ReactNode;
  dots?: CardDot[];
  showInfoIcon?: boolean;
  breakdown?: CardBreakdown[];
  tootlipContent?: string;
  icon?: React.ReactNode;
}

export const PortfolioCard: React.FC<PortfolioCardData> = (props) => {
  const { title, value, dots, breakdown, showInfoIcon, icon, tootlipContent } =
    props;
  const [showTooltip, setShowTooltip] = React.useState(false);

  return (
    <div className="cx-bg-background-default cx-border cx-border-neutral-light cx-shadow-sm cx-rounded-modal cx-p-6 cx-flex cx-flex-col cx-w-fit cx-min-w-full">
      {/* Icon top-left */}
      <div className="cx-flex cx-items-start cx-mb-1">
        {icon ? <span className="cx-text-text-tertiary">{icon}</span> : null}
      </div>

      {/* Title with inline info icon */}
      <div className="cx-flex cx-items-center cx-gap-2 cx-mb-2">
        <div className="cx-text-text-tertiary cx-text-sm cx-font-medium">
          {title}
        </div>
        {showInfoIcon ? (
          tootlipContent ? (
            <span
              className="cx-relative cx-inline-flex cx-items-center"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setShowTooltip(false)}
              tabIndex={0}
            >
              <RoundQuestionMarkIcon className="cx-text-text-tertiary cx-w-4 cx-h-4" />
              {showTooltip ? (
                <span
                  role="tooltip"
                  className="cx-absolute cx-top-full cx-left-1/2 -cx-translate-x-1/2 cx-mt-2 cx-z-50 cx-rounded-full cx-px-3 cx-py-1 cx-text-xs cx-font-medium cx-text-white cx-whitespace-nowrap cx-shadow-md cx-pointer-events-none"
                  style={{
                    backgroundImage:
                      "linear-gradient(134deg, #EDA3FC 0%, #7553DB 31.97%, #2529EB 66.58%, #01B2FF 100%)",
                  }}
                >
                  {tootlipContent}
                </span>
              ) : null}
            </span>
          ) : (
            <RoundQuestionMarkIcon className="cx-text-text-tertiary cx-w-4 cx-h-4" />
          )
        ) : null}
      </div>

      {/* Amount with dots inline; breakdown position unchanged */}
      <div className="cx-flex cx-items-center cx-gap-4 cx-min-w-0">
        <div className="cx-text-2xl cx-font-extrabold">{value}</div>
        {dots && dots.length > 0 ? (
          <div className="cx-flex cx-items-center cx-gap-4 cx-text-xs cx-text-text-secondary cx-ml-auto">
            {dots.map((d, i) => (
              <div key={i} className="cx-flex cx-items-center cx-gap-1">
                <span
                  className={`cx-inline-block cx-w-1 cx-h-1 cx-rounded-full ${d.colorClass}`}
                />
                <span>{d.label}</span>
              </div>
            ))}
          </div>
        ) : null}
        {breakdown ? (
          <div className="cx-ml-auto md:cx-flex cx-hidden cx-flex-col cx-gap-2 cx-text-xs cx-text-text-secondary">
            {breakdown.map((b, i) => (
              <div key={i} className="cx-flex cx-items-center cx-gap-2">
                <span
                  className={`cx-inline-block cx-w-1 cx-h-1 cx-rounded-full ${b.colorClass}`}
                />
                <span>{b.label}: </span>
                <span className="cx-font-medium">{b.amount}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Removed old separate dots block */}

      {breakdown ? (
        <div className="cx-mt-4 md:cx-hidden cx-text-xs cx-text-text-secondary cx-flex cx-flex-col cx-gap-2">
          {breakdown.map((b, i) => (
            <div key={i} className="cx-flex cx-items-center cx-gap-1">
              <span
                className={`cx-inline-block cx-w-1 cx-h-1 cx-rounded-full ${b.colorClass}`}
              />
              <span>
                {b.label}: <span className="cx-font-medium">{b.amount}</span>
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default PortfolioCard;
